/* global CustomEvent */
import { set, unset } from "../../js/lib/util";
import store from "../../js/lib/store";
import cart from "../../js/lib/cart";
import { createApp, defineComponent } from "vue";
import TextInput from "../text-input/text-input.vue";
import QuantitySelector from "../quantity-selector/quantity-selector.vue";

export const componentDefinition = defineComponent({
  name: "product-form",
  components: {
    TextInput,
    "text-input": TextInput,
    QuantitySelector,
    "quantity-selector": QuantitySelector,
  },
  data(): {
    quantity: number;
    selectedSellingPlanDiscount: number | null;
    selectedSellingPlanPrice: string | "";
    selected: any;
  } {
    return {
      quantity: 1,
      selectedSellingPlanDiscount: null,
      selectedSellingPlanPrice: "",
      selected: {},
    };
  },
  mounted() {
    try {
      this.pickActiveVariant();
      this.calculateSellingPlanPrice();
    } catch (e) {
      console.error(e);
    }
  },
  methods: {
    getFormEl(): HTMLFormElement | null {
      const root = this.$el as Node;

      if (root instanceof HTMLFormElement) {
        return root;
      }

      if (root instanceof Element) {
        const nested = root.querySelector("form.product-form__el");
        if (nested instanceof HTMLFormElement) {
          return nested;
        }
      }

      const container = root.parentElement;
      const form = container?.querySelector("form.product-form__el");
      if (form instanceof HTMLFormElement) {
        return form;
      }

      return document.querySelector(".product-form form.product-form__el");
    },
    calculateSellingPlanPrice() {
      const form = this.getFormEl();

      if (!form) {
        return;
      }

      try {
        const selectedSellingPlan = form.querySelector(
          'select[name="selling_plan"] option:checked'
        );

        if (selectedSellingPlan) {
          const selectedSellingPlanDiscount = JSON.parse(
            selectedSellingPlan.getAttribute("data-discount") ?? ""
          );

          const product = (window as any).BARREL.product;
          const firstAvailableVariant = product.variants[0];
          const basePrice =
            this.selected && this.selected.price
              ? this.selected.price
              : firstAvailableVariant.price;

          if (!basePrice) {
            this.selectedSellingPlanPrice = "";
            return;
          }

          const adjust =
            selectedSellingPlanDiscount.price_adjustments[0]?.value ?? 0;
          this.selectedSellingPlanDiscount = adjust;

          const adjusted = (
            (basePrice / 100) *
            ((100 - adjust) / 100)
          ).toFixed(2);

          if (Number.isNaN(Number(adjusted))) {
            this.selectedSellingPlanPrice = "";
            return;
          }

          this.selectedSellingPlanPrice = adjusted.replace(".00", "");
        }
      } catch (e) {
        console.error(e);
      }
    },
    showModal(modal: string) {
      set(document.body, "showing-modal");
      set(
        document.querySelector(`.js-modal[data-modal=${modal}]`) as HTMLElement,
        "active"
      );
    },
    triggerNotifyMe(e: Event) {
      e.preventDefault();
      e.stopPropagation();

      const outOfStockElement = document.querySelector(
        "[data-out-of-stock-url]"
      ) as HTMLAnchorElement;

      if (outOfStockElement) {
        const outOfStockUrl =
          outOfStockElement.getAttribute("data-out-of-stock-url");

        if (outOfStockUrl && outOfStockUrl.includes("window.")) {
          eval(outOfStockUrl);
        } else {
          this.showModal("notify-me");
        }
      } else {
        this.showModal("notify-me");
      }

      return false;
    },
    hideModal(modal: string) {
      unset(document.body, "showing-modal");
      unset(
        document.querySelector(
          `.js-modal[data-modal="${modal}"]`
        ) as HTMLElement,
        "active"
      );
    },
    pickActiveVariant() {
      const product = (window as any).BARREL.product;
      const { variants, options } = product;

      for (let i = 0; i < variants.length; i++) {
        const variant = variants[i];
        const isMatch = options.every((option: string, _i: number) => {
          return variant[`option${_i + 1}`] === this.selected[option];
        });

        if (isMatch) {
          store.activeVariant = variant;
          this.selected = variant;
          break;
        }
      }

      if (!store.activeVariant) {
        store.activeVariant = variants[0];
        this.selected = variants[0];
      }
    },
    updateSelectedOption(name: string, value: string) {
      this.selected[name] = value;
    },
    addToCart() {
      if (!this.selected) {
        return;
      }

      const form = this.getFormEl();
      const item = this.selected;

      if (!form) {
        return;
      }

      form.classList.add("loading");

      const idInput = form.querySelector<HTMLInputElement>("input[name=id]");
      const quantityInput =
        form.querySelector<HTMLInputElement>("input[name=quantity]");

      const formData: {
        items: Array<{
          id: string;
          quantity: number | string;
          selling_plan?: string;
        }>;
      } = {
        items: [
          {
            id: idInput?.value ?? "",
            quantity: quantityInput?.value ?? 1,
          },
        ],
      };

      const hasSellingPlanInput = form.querySelector<HTMLInputElement>(
        'input[name="has_selling_plan"]:checked'
      );
      const hasSellingPlan =
        hasSellingPlanInput != null && hasSellingPlanInput.value === "yes";
      const selectedSellingPlan = form.querySelector<HTMLSelectElement>(
        'select[name="selling_plan"]'
      );

      let isSubscription = false;

      if (hasSellingPlan && selectedSellingPlan) {
        isSubscription = true;
        formData.items[0].selling_plan = selectedSellingPlan.value;
      }

      const shopifyRoot = (window as any).Shopify?.routes?.root ?? "/";
      const cartAddUrl = `${shopifyRoot}cart/add.js`;

      fetch(cartAddUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      })
        .then(async (response) => {
          const data = await response.json();

          if (!response.ok || data?.status === 422 || data?.errors || data?.message) {
            form?.classList.remove("loading");
            return;
          }

          const cartData = await cart.get();
          store.refreshCart(cartData);
          store.updateLastAdd(Date.now());
          store.updateIsMiniCartOpen(true);
          form?.classList.remove("loading");

          const quantity = quantityInput?.value || 1;

          document.dispatchEvent(
            new CustomEvent("product:added", {
              detail: {
                variant: item,
                subscription: isSubscription,
                quantity: quantity,
              },
            })
          );
        })
        .catch((error) => {
          console.error(error);
          form?.classList.remove("loading");
        });
    },
  },
});

export default (el: HTMLElement) => {
  const template = el.innerHTML;
  el.innerHTML = "";
  const instanceApp = createApp(
    defineComponent({
      ...componentDefinition,
      template,
    } as any)
  );
  instanceApp.component("text-input", TextInput);
  instanceApp.component("quantity-selector", QuantitySelector);
  return instanceApp.mount(el);
};
